import React from 'react';
import { FaEdit, FaAngleUp, FaTimes, FaSave, FaSpinner } from 'react-icons/fa';

const MinimizedEditForm = ({
  record,
  onClick,
  theme = {},
  primaryKey,
  position = 0, // Position in the stack (0 is the lowest visually, highest z-index)
  totalMinimized = 1,
  columnProps = [], // Receive column configuration
  // NUOVI: Props per le azioni di salvataggio e annullamento
  onSave,
  onCancel,
  isProcessing = false,
  singularRecordName = "Record",
  // NUOVO: Per evidenziare se questo record è attualmente in modifica
  isCurrentlyEditing = false,
  // NUOVO: Mostra solo la toolbar senza header informativo
  showOnlyToolbar = false
}) => {
  if (!record) return null;

  const themeColorName = theme?.themeColor || 'primary';
  const recordId = record[primaryKey];
  const isDark = document.body.classList.contains('dark');

  let explicitBackgroundColor;
  if (isDark) {
    // Dark mode background colors (opaque)
    if (themeColorName === 'emerald') {
      explicitBackgroundColor = '#065f46'; // emerald-700 (adjust as needed for desired darkness)
    } else if (themeColorName === 'indigo') {
      explicitBackgroundColor = '#4338ca'; // indigo-700
    } else {
      explicitBackgroundColor = '#374151'; // gray-700
    }
  } else {
    // Light mode background colors (opaque)
    if (themeColorName === 'emerald') {
      explicitBackgroundColor = '#d1fae5'; // emerald-100
    } else if (themeColorName === 'indigo') {
      explicitBackgroundColor = '#e0e7ff'; // indigo-100
    } else {
      explicitBackgroundColor = '#f3f4f6'; // gray-100
    }
  }

  // --- Find the third VISIBLE column for the title ---
  const visibleColumns = columnProps.filter(col => col && col.visibility > 1 && !col.isPrimaryKey && col.field !== primaryKey);
  let displayLabel = primaryKey;
  let representativeValue = recordId;

  if (visibleColumns.length >= 3) {
    const thirdColumn = visibleColumns[2];
    displayLabel = thirdColumn.label || thirdColumn.field;
    representativeValue = record[thirdColumn.field];
  } else if (visibleColumns.length > 0) {
    const firstColumn = visibleColumns[0];
    displayLabel = firstColumn.label || firstColumn.field;
    representativeValue = record[firstColumn.field];
  }
  // --- End finding third column ---

  // --- Styling for Visibility & Stacking ---
  const baseZIndex = 60;
  const zIndex = baseZIndex + (totalMinimized - 1 - position); // Higher position = lower z-index (appears underneath)
  const bottomOffset = position * 28; // Increased vertical spacing significantly
  const rightOffset = 16 + (position * 8); // Subtle horizontal offset

  // Slightly change opacity/border based on position for visual depth
  const opacity = 1 - (position * 0.05); // Reduce opacity slightly for lower items
  const borderOpacity = Math.max(100 - (position * 10), 50); // Reduce border opacity

  // Se showOnlyToolbar è true, mostra solo la toolbar
  if (showOnlyToolbar) {
    return (
      <div className="flex space-x-2 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-600">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onCancel && onCancel(record);
          }}
          disabled={isProcessing}
          className="px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition disabled:opacity-50 flex items-center"
          title="Annulla modifiche"
        >
          <FaTimes className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSave && onSave(record);
          }}
          disabled={isProcessing}
          className={`px-2.5 py-1.5 text-sm bg-${themeColorName}-600 text-white rounded hover:bg-${themeColorName}-700 transition disabled:opacity-50 flex items-center`}
          title={`Salva ${singularRecordName}`}
        >
          {isProcessing ? (
            <FaSpinner className="animate-spin h-3.5 w-3.5" />
          ) : (
            <FaSave className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
    );
  }

  // Modalità completa (per backward compatibility, se mai servisse)
  return (
    <div
      className={`
        fixed right-4 rounded-lg shadow-lg
        border-2 ${isCurrentlyEditing 
          ? `border-${themeColorName}-600 dark:border-${themeColorName}-400 ring-2 ring-${themeColorName}-300 dark:ring-${themeColorName}-500` 
          : `border-${themeColorName}-400 dark:border-${themeColorName}-600`
        }
        text-${themeColorName}-800 dark:text-gray-100
        transition-all duration-200
        ${isCurrentlyEditing ? 'transform scale-105' : ''}
      `}
      style={{
        bottom: `${bottomOffset}px`,
        right: `${rightOffset}px`,
        zIndex: zIndex,
        maxWidth: '320px',
        minWidth: '280px',
        opacity: opacity,
        backgroundColor: explicitBackgroundColor,
      }}
    >
      {/* Header con informazioni record */}
      <div 
        onClick={onClick}
        className="flex items-center justify-between p-2 px-3 cursor-pointer hover:brightness-110 border-b border-gray-200 dark:border-gray-600"
        title={`${isCurrentlyEditing ? 'In modifica - ' : ''}Ripristina modifica per ID: ${recordId}`}
      >
        <div className="flex items-center overflow-hidden mr-2">
          <FaEdit className={`mr-2 flex-shrink-0 text-${themeColorName}-600 dark:text-${themeColorName}-300 ${isCurrentlyEditing ? 'animate-pulse' : ''}`} />
          <span className={`font-semibold truncate text-${themeColorName}-700 dark:text-${themeColorName}-100 text-sm`}>
            {isCurrentlyEditing && '● '}{displayLabel}:
          </span>
          <span className="ml-1.5 truncate dark:text-gray-200 text-sm">{String(representativeValue ?? '-')}</span>
        </div>
        <FaAngleUp className={`text-${themeColorName}-600 dark:text-${themeColorName}-300 flex-shrink-0`} />
      </div>

      {/* Toolbar con pulsanti Annulla e Salva */}
      <div className="flex justify-end space-x-2 p-2 bg-gray-50 dark:bg-gray-700/50">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onCancel && onCancel(record);
          }}
          disabled={isProcessing}
          className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition disabled:opacity-50 flex items-center"
          title="Annulla modifiche"
        >
          <FaTimes className="mr-1 h-3 w-3" />
          Annulla
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSave && onSave(record);
          }}
          disabled={isProcessing}
          className={`px-3 py-1.5 text-sm bg-${themeColorName}-600 text-white rounded-md hover:bg-${themeColorName}-700 transition disabled:opacity-50 flex items-center`}
          title={`Salva ${singularRecordName}`}
        >
          {isProcessing ? (
            <FaSpinner className="animate-spin mr-1 h-3 w-3" />
          ) : (
            <FaSave className="mr-1 h-3 w-3" />
          )}
          Salva
        </button>
      </div>
    </div>
  );
};

export default MinimizedEditForm;